import { Borrower } from 'app/modules/borrower/models/borrower/borrower.model';
import { Component, OnInit, ViewChild, Input, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Document } from 'app/models/dto/document.model';
import { DocumentCategory } from 'app/models/dto/document-category.model';
import { DocumentsComponent } from 'app/documents/documents/documents.component';
import { DocumentsService } from 'app/services/documents/documents.service';
import { UpdateDocument, UpdateDocumentDrsVerification, UploadDocument, SubscribeToBorrowerDocuments, GetViewableMimetypes, GetDocumentCategories, UpdateDocumentAlert, RunExperianCommercial } from 'app/store/documents/document.actions';
import { DocumentState } from 'app/store/documents/document.state';
import { Observable, BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { Store, Select } from '@ngxs/store';
import { map } from 'rxjs/operators';
import { environment } from 'environments/environment';
import * as _ from 'lodash';
import { LenderUserState } from 'app/store/lender-user/lender-user/lender-user.state';
import { LenderUser } from 'app/models/dto/lender-user.model';
import { Alert } from 'app/models/dto/alert.model';
import { DismissOcrolusAlert, GetOcrolusActivity, SendToOcrolus } from 'app/store/ocrolus/ocrolus.actions';
import { OcrolusState } from 'app/store/ocrolus/ocrolus.state';

@Component({
    selector: 'app-documents-smart',
    templateUrl: './documents-smart.component.html',
    styleUrls: ['./documents-smart.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DocumentsSmartComponent implements OnInit {

    @Select(DocumentState.documents) documents$: Observable<Document[]>;
    @Select(DocumentState.documentCategories) documentCategories$: Observable<DocumentCategory[]>;
    @Select(DocumentState.viewableMimetypes) viewableMimetypes$: Observable<any>;
    @Select(DocumentState.alert) documentAlert$: Observable<Alert>;
    @Select(DocumentState.isUploading) isUploading$: Observable<boolean>;
    @Select(LenderUserState.lenderUser) lenderUser$: Observable<LenderUser>;
    @Select(OcrolusState.ocrolusAlert) ocrolusAlert$: Observable<LenderUser>;
    @Select(OcrolusState.sending) sending$: Observable<boolean>;

    @Input() compactView: boolean = false;
    @Input() borrower: Borrower;
    @Input() tableColumns: string[] = [
        'select',
        'title',
        'category',
        'for',
        'createdTimestamp',
        'scan-status',
        'action'
    ];
    @Input() showActions: boolean = true;
    @Input() showUploadedBy: boolean = true;
    @Input() defaultPageSize: number = 100;
    @Input() showPageSizeOptions: boolean = true;

    @ViewChild('documentsComponent') documentsComponent: DocumentsComponent;

    canProcessDocs$: Observable<boolean>;
    canUseDRSDocumentVerify$: Observable<boolean>;
    isFundingDesk$: Observable<boolean>;
    destroyed$ = new Subject<void>();
    isPullingCredit: boolean;
    uploadedPercent = 0;
    uploadProgress: string;
    ocrolusEligible: boolean;
    canSoftPullCredit$: Observable<boolean>;
    borrowerBS: BehaviorSubject<Borrower>;
    creditPullDisableMessage: string;
    viewableDocuments$: Observable<Document[]>;  // Filtered for sesitive docs (KYC/KYB)

    ocrolusAlert: {
        type: string;
        message: string;
    } | undefined | null = null;

    creditAlert: {
        level: string;
        message: string;
    } | null = null;

    constructor(
        private store: Store,
        private documentsService: DocumentsService,
        private cdRef: ChangeDetectorRef,
    ) { }

    ngOnInit() {
        // Currently going to allow all mimeTypes to be viewable --- might need to do further cleanup on this later
        this.store.dispatch(new GetViewableMimetypes());
        this.store.dispatch(new SubscribeToBorrowerDocuments(this.borrower.id));

        this.viewableDocuments$ = combineLatest([
            this.lenderUser$,
            this.documents$
        ]).pipe(
            // eslint-disable-next-line max-len
            map(([lenderUser, documents]) => lenderUser.permissions.includes('canSeeDocValidation') ? documents : documents.filter(doc => doc['category'] !== 'kycKybValidation'))
        );

        this.viewableDocuments$.subscribe(documents => {
            if (documents.length > 0) {
                this.handleCheckOcrolusStatus(documents);
            }
        });

        this.borrowerBS = new BehaviorSubject(this.borrower);
        this.canSoftPullCredit$ = combineLatest([
            this.lenderUser$,
            this.borrowerBS
        ]).pipe(
            map(([lenderUser, borrower]) => this.softPullCreditEnabled(lenderUser.permissions.includes('softPullCredit'), borrower))
        );
        this.cdRef.markForCheck();

        this.canProcessDocs$ = this.lenderUser$.pipe(
            map(currentUser => currentUser.permissions.includes('submitToOcrolus'))
        );

        this.canUseDRSDocumentVerify$ = this.lenderUser$.pipe(
            map(currentUser => currentUser.permissions.includes('canUseDRSDocumentVerify'))
        );

        this.isFundingDesk$ = this.lenderUser$.pipe(
            map(currentUser => currentUser.roles.includes('fundingDesk'))
        );

        if (!this.borrower.creditPullStale) {
            this.creditPullDisableMessage = 'Credit has been pulled within the last 30 days';
        } else if (!this.borrower.applicationSignedRecently) {
            this.creditPullDisableMessage = 'Application has not been signed within the last 30 days';
        } else if (environment.TENANT_CREDIT_BUTTON_DISABLE) {
            this.creditPullDisableMessage = 'Pull Credit disabled by env variable';
        }
    }

    softPullCreditEnabled(isPermitted: boolean, borrower: Borrower): boolean {
        return (isPermitted && borrower.creditPullStale && borrower.applicationSignedRecently);
    }

    handleUpdateDocument(document: Document): void {
        this.store.dispatch(new UpdateDocument(document));
    }

    handleUpdateDocumentDrsVerification(document: Document): void {
        this.store.dispatch(new UpdateDocumentDrsVerification(document));
    }


    handleProcessDocuments(docIds: number[]): void {
        this.store.dispatch(new SendToOcrolus(this.borrower.id, docIds, true));
    }

    handleUploadDocument(documents: FileList) {
        let totalUploaded = 0;
        _.forEach(documents, (document => {
            this.uploadProgress = 'Uploading ' + totalUploaded + ' of ' + documents.length;
            this.store.dispatch(new UploadDocument(document, this.borrower.id))
                .subscribe({
                    next: () => {
                        totalUploaded++;
                        this.uploadProgress = 'Uploading ' + totalUploaded + ' of ' + documents.length;
                        this.uploadedPercent = totalUploaded / documents.length * 100;
                        if (totalUploaded === documents.length) {
                            this.uploadedPercent = 0;
                        }
                    }
                });
        }));
    }

    handleCheckOcrolusStatus(documents: Document[]) {
        this.store.dispatch(new GetOcrolusActivity(this.borrower.id, documents));
    }

    dismissOcrolusAlert() {
        this.store.dispatch(new DismissOcrolusAlert(this.borrower.id));
    }

    dismissDocumentAlert() {
        this.store.dispatch(new UpdateDocumentAlert(null));
    }

    handleRepullCredit(borrowerId: number) {
        this.isPullingCredit = true;
        this.documentsService.repullCredit(borrowerId).subscribe({
            next: () => {
                this.isPullingCredit = false;
                this.creditAlert = {
                    level: 'success',
                    message: 'Successfully repulled credit.'
                };
                this.borrower.creditPullStale = false;
                this.borrowerBS.next(this.borrower);
                this.cdRef.markForCheck();
            },
            error: err => {
                this.creditAlert = {
                    level: 'error',
                    message: 'Credit retrieval failed: ' + ((err && err.message) || 'please contact support team')
                };
                this.isPullingCredit = false;
                this.cdRef.markForCheck();

                return err;
            }
        });
    }

    handleExperianCommercialCreditPull(borrowerId: number) {
        this.store.dispatch(new RunExperianCommercial(borrowerId));
        this.cdRef.markForCheck();
    }

    dismissCreditAlert() {
        this.creditAlert = null;
        this.cdRef.markForCheck();
    }
}
